import React, { useRef, useState } from "react";
import moment from "moment";
import { Constants } from "@eagerdog/constants";
import {
  resolveApiPath,
  validateScore,
  ScoringContext,
} from "@eagerdog/scoring-core";

import {
  IShow,
  IEventSummary,
  INoseworkScore,
  IFastcatScore,
  IFastdashScore,
  ICatScore,
  IFetchScore,
  RatRaceScoreDto,
  ScentworkScoreDto,
  DockJumpingScoreDto,
  ShedDogScoreDto,
  PrecisionCoursingScoreDto,
  WeightPullScoreDto,
  FarmDogScoreDto,
  WaterDogScoreDto,
  AgilityScoreDto,
  AkcAgilityScoreDto,
  HerdingScoreDto,
  ScentScoreDto,
  ScentDogGamesScoreDto,
  IBarnHuntScore,
} from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";

import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";

import NoseworkResults from "./NoseworkResults/NoseworkResults";
import FastCatResults from "./FastCatResults/FastCatResults";
import FastDashResults from "./FastDashResults/FastDashResults";
import CATResults from "./CATResults/CATResults";
import FetchResults from "./FetchResults/FetchResults";
import UrbanRatRaceResults from "./UrbanRatRaceResults/UrbanRatRaceResults";
import CountryRatRaceResults from "./CountryRatRaceResults/CountryRatRaceResults";
import LostAndFoundResults from "./LostAndFoundResults/LostAndFoundResults";
import RallyObedienceResults from "./RallyObedienceResults/RallyObedienceResults";
import ObedienceResults from "./ObedienceResults/ObedienceResults";
import ScentworkResults from "./ScentworkResults/ScentworkResults";
import DockJumpingResults from "./DockJumpingResults/DockJumpingResults";
import ShedDogResults from "./ShedDogResults/ShedDogResults";
import PrecisionCoursingResults from "./PrecisionCoursingResults/PrecisionCoursingResults";
import WeightPullResults from "./WeightPullResults/WeightPullResults";
import FarmDogResults from "./FarmDogResults/FarmDogResults";
import WaterDogResults from "./WaterDogResults/WaterDogResults";
import AgilityResults from "./AgilityResults/AgilityResults";
import AkcAgilityResults from "./AkcAgilityResults/AkcAgilityResults";
import HerdingResults from "./HerdingResults/HerdingResults";
import ScentResults from "./ScentResults";
import BarnHuntResults from "./BarnHuntResults/BarnHuntResults";
import ScentDogGamesResults from "./ScentDogGamesResults/ScentDogGamesResults";
import CWagsObedienceResults from "./CWagsObedienceResults/CWagsObedienceResults";
import CWagsScentResults from "./CWagsScentResults/CWagsScentResults";
import CWagsRallyResults from "./CWagsRallyResults/CWagsRallyResults";

import "./EnterResults.scss";

interface IProps {
  showType: string,
  event: IEventSummary,
  result: any,
  eventId: string,
  show?: IShow,
  // Other scores in the same show; used by scentwork to default hides/time
  // limit from an already-entered dog when the judge schedule has no values.
  showScores?: any[],
  onSave(): void,
  onClose(): void
}

const EnterResults: React.FC<IProps> = (props) => {
  const [judgeApproval, setJudgeApproval] = useState<boolean>(false);
  const [judgeName, setJudgeName] = useState<string>(props.result.judge_name ?? "");
  const [judgeNumber, setJudgeNumber] = useState<string>(props.result.judge_number ?? "");

  const [results, setResults] = useState<any>();
  const [scentworkResults, setScentworkResults] = useState<any[]>([]);
  const [show, setShow] = useState<IShow>();
  const catResultsRef = useRef<ICatScore | undefined>(undefined);

  // Get element configuration for scentwork shows
  const getScentworkElementConfig = () => {
    if (props.showType !== Constants.show_type.scentwork || !props.show || !props.result.dog_class) {
      return null;
    }

    const element = props.show.show_elements.find((el: any) => 
      el.show_element === props.result.dog_class.show_element &&
      el.level === props.result.dog_class.level
    );

    if (!element) return null;

    return {
      num_hides: element.num_hides || 0,
      time_limit: element.time_limit || ""
    };
  };

  const scentworkConfig = getScentworkElementConfig();

  const isScratchSelection = () => {
    if (props.showType === Constants.show_type.scentwork) {
      return scentworkResults.some(
        (scentworkResult) =>
          scentworkResult?.result_type === Constants.score_result_type.scratched
      );
    }
    return results?.result_type === Constants.score_result_type.scratched;
  };

  const isConflictSelection = () => {
    if (props.showType === Constants.show_type.scentwork) {
      return scentworkResults.some(
        (scentworkResult) =>
          scentworkResult?.result_type === Constants.score_result_type.conflict
      );
    }
    return results?.result_type === Constants.score_result_type.conflict;
  };

  // Submission is consolidated through @eagerdog/scoring-core: the endpoint
  // suffix is resolved from the show type + context, then sent via the generic
  // apiService.enterScore. This keeps web and mobile submitting to identical
  // routes from one source of truth.
  const scoringCtx: ScoringContext = {
    sanctioningClub: props.event.sanctioning_club,
    dogClass: props.result.dog_class,
    show: show,
  };

  const submitScore = (payload: any) => {
    const path = resolveApiPath(props.showType, scoringCtx);

    if (!path) {
      toast.show({
        title: "Enter Results",
        content: "Scoring for this show type isn't supported.",
        duration: 10000,
        type: "fail"
      });
      return;
    }

    apiService
      .enterScore(props.eventId, props.result._id, path, payload)
      .then(() => {
        props.onSave();
      })
      .catch((error) => {
        toast.show({
          title: "Enter Results",
          content: error.response.data.message ? error.response.data.message : "Something went wrong when saving results",
          duration: 10000,
          type: "fail"
        });
      });
  };

  const enterResults = () => {
    if (isScratchSelection()) {
      apiService
        .updateScoreScratch(props.eventId, props.result._id, true)
        .then(() => {
          props.onSave();
        })
        .catch((error) => {
          toast.show({
            title: "Enter Results",
            content: error.response.data.message
              ? error.response.data.message
              : "Something went wrong when scratching this run",
            duration: 10000,
            type: "fail",
          });
        });
      return;
    }

    if (isConflictSelection()) {
      apiService
        .updateScoreConflict(props.eventId, props.result._id, true)
        .then(() => {
          props.onSave();
        })
        .catch((error) => {
          toast.show({
            title: "Enter Results",
            content: error.response.data.message
              ? error.response.data.message
              : "Something went wrong when flagging this run as a conflict",
            duration: 10000,
            type: "fail",
          });
        });
      return;
    }

    // Build the payload for this show type, then validate it through the shared
    // pipeline in @eagerdog/scoring-core (the same rules mobile uses). Special
    // payloads: scentwork is an array of per-hide results; cat reads from its
    // ref; fetch / farm dog merge the judge approval + credentials.
    let payload: any = results;
    if (props.showType === Constants.show_type.scentwork) {
      payload = scentworkResults;
    } else if (props.showType === Constants.show_type.cat) {
      payload = (catResultsRef.current ?? results) as ICatScore;
    } else if (
      props.showType === Constants.show_type.fetch ||
      props.showType === Constants.show_type.farm_dog
    ) {
      payload = {
        ...results,
        approved_by_judge: judgeApproval,
        judge_name: judgeName,
        judge_number: judgeNumber,
      };
    }

    const isMulti = Array.isArray(payload);
    const validation = isMulti
      ? validateScore(props.showType, undefined, scoringCtx, payload)
      : validateScore(props.showType, payload, scoringCtx);

    if (validation.valid) {
      submitScore(payload);
    } else {
      toast.show({
        title: "Enter Results",
        content: validation.errors.map((e) => e.message).join("\n"),
        duration: 10000,
        type: "fail"
      });
    }

    if (show !== undefined) {
      apiService.updateShow(show.event_id, show._id, show);
    }
  }

  const renderResults = () => {
    switch(props.showType) {
      case Constants.show_type.nosework:
        return <NoseworkResults result={props.result} onChange={(newResults: INoseworkScore) => {
          setResults(newResults);
        }} />;
      case Constants.show_type.fast_cat:
        return <FastCatResults result={props.result} onChange={(newResults: IFastcatScore) => {
          setResults(newResults);
        }} />;
      case Constants.show_type.fast_dash:
        return <FastDashResults result={props.result} onChange={(newResults: IFastdashScore) => {
          setResults(newResults);
        }} />;
      case Constants.show_type.cat:
        return <CATResults result={props.result} onChange={(newResults: ICatScore) => {
          catResultsRef.current = newResults;
          setResults(newResults);
        }} />;
      case Constants.show_type.fetch:
        return <FetchResults result={props.result} onChange={(newResults: IFetchScore, newJudgeApproval: boolean, judgeName: string, judgeNumber: string) => {
          setJudgeApproval(newJudgeApproval);
          setJudgeName(judgeName);
          setJudgeNumber(judgeNumber);
          setResults(newResults);
        }} />;
      case Constants.show_type.urban_rat_race:
        return <UrbanRatRaceResults result={props.result} onChange={(newResults: RatRaceScoreDto) => {
          setResults(newResults);
        }} />;
      case Constants.show_type.country_rat_race:
        return <CountryRatRaceResults result={props.result} onChange={(newResults: RatRaceScoreDto) => {
          setResults(newResults);
        }} />;
      case Constants.show_type.lost_and_found:
        return <LostAndFoundResults result={props.result} onChange={(newResults: RatRaceScoreDto) => {
          setResults(newResults);
        }} />;
        case Constants.show_type.rally_obedience:
        // Use simplified form for C-WAGS Rally
        if (props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS) {
          return <CWagsRallyResults result={props.result} onChange={(newResults: any) => {
            setResults(newResults);
          }} />;
        }
        return <RallyObedienceResults event={props.event} onExerciseChange={props.onSave} result={props.result} onChange={(newResults: any, show?: IShow) => {
          if (show) {
            setShow(show);
          }

          setResults(newResults);
        }} />;
      case Constants.show_type.obedience:
        // Use simplified form for C-WAGS Obedience
        if (props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS) {
          return <CWagsObedienceResults result={props.result} onChange={(newResults: any) => {
            setResults(newResults);
          }} />;
        }
        return <ObedienceResults result={props.result} onChange={(newResults: any, show?: IShow) => {
          if (show) {
            setShow(show);
          }

          setResults(newResults);
        }} />;
      case Constants.show_type.scentwork:
        return <ScentworkResults result={props.result} show={props.show} showScores={props.showScores} onChange={(newResults: ScentworkScoreDto[]) => {
          setScentworkResults(newResults);
        }} />;
      case Constants.show_type.dock_jumping:
        return <DockJumpingResults result={props.result} onChange={(newResults: DockJumpingScoreDto) => {
          setResults(newResults);
        }} />
      case Constants.show_type.shed_dog:
        return <ShedDogResults result={props.result} onChange={(newResults: ShedDogScoreDto) => {
          setResults(newResults);
        }} />
      case Constants.show_type.precision_coursing:
        return <PrecisionCoursingResults result={props.result} onChange={(newResults: PrecisionCoursingScoreDto) => {
          setResults(newResults);
        }} />
      case Constants.show_type.weight_pull:
        return <WeightPullResults result={props.result} onChange={(newResults: WeightPullScoreDto) => {
          setResults(newResults);
        }} />
      case Constants.show_type.farm_dog:
        return <FarmDogResults result={props.result} onChange={(newResults: FarmDogScoreDto, judgeName: string, judgeNumber: string, judgeApproval: boolean) => {
          setJudgeName(judgeName);
          setJudgeNumber(judgeNumber);
          setJudgeApproval(judgeApproval);
          setResults(newResults);
        }} />;
      case Constants.show_type.water_dog:
        return <WaterDogResults result={props.result} onChange={(newResults: WaterDogScoreDto) => {
          setResults(newResults);
        }} />;
      case Constants.show_type.agility:
        if (props.event.sanctioning_club === Constants.sanctioning_club.UKC) {
          return <AgilityResults show={props.show} result={props.result} onChange={(newResults: AgilityScoreDto) => {
            setResults(newResults);
          }} />;
        }
        if (props.event.sanctioning_club === Constants.sanctioning_club.AKC) {
          return <AkcAgilityResults show={props.show} result={props.result} onChange={(newResults: AkcAgilityScoreDto) => {
            setResults(newResults);
          }} />;
        }
        return null;
      case Constants.show_type.herding:
      case Constants.show_type.pre_trial_herding:
      case Constants.show_type.instinct_test_herding:
        return <HerdingResults show={props.show} showType={props.showType} result={props.result} onChange={(newResults: HerdingScoreDto) => {
          setResults(newResults);
        }} />;
      case Constants.show_type.scent:
        // Use simplified form for C-WAGS Scent
        if (props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS) {
          return <CWagsScentResults result={props.result} onChange={(newResults: any) => {
            setResults(newResults);
          }} />;
        }
        return <ScentResults result={props.result} onChange={(newResults: ScentScoreDto) => {
          setResults(newResults);
        }} />;
      case Constants.show_type.barn_hunt:
        return <BarnHuntResults result={props.result} onChange={(newResults: IBarnHuntScore) => {
          setResults(newResults);
        }} />;
      case Constants.show_type.scent_dog_games:
        return <ScentDogGamesResults result={props.result} onChange={(newResults: ScentDogGamesScoreDto) => {
          setResults(newResults);
        }} />;
    }
  }

  // Determine if we should show the element (only if different from show_type)
  const getClassDisplay = () => {
    const dogClass = props.result.dog_class;
    if (!dogClass) return null;
    
    const element = dogClass.show_element;
    const level = dogClass.level;
    const section = dogClass.section;
    const showType = props.showType;
    
    // If element matches show type (case insensitive, with underscores converted to spaces)
    const normalizedElement = element?.toLowerCase().replace(/_/g, ' ');
    const normalizedShowType = showType?.toLowerCase().replace(/_/g, ' ');
    const normalizedLevel = level?.toLowerCase().replace(/_/g, ' ');
    
    // CAT and Fast CAT have no separate class. The element and level both repeat the show type.
    if (showType === Constants.show_type.cat || showType === Constants.show_type.fast_cat) {
      return null;
    }
    
    let display: string | null;
    if (normalizedElement === normalizedShowType || !element) {
      // Just show level
      display = level || null;
    } else {
      // Show element - level
      display = level ? `${element} - ${level}` : element;
    }

    // Append section (e.g. 4-H Agility's "Division A"/"Division B") when present
    if (display && section) {
      display = `${display} - ${section}`;
    }

    return display;
  };

  const classDisplay = getClassDisplay();

  // Get CAT-specific info for display in header
  const getCATInfo = () => {
    if (props.showType !== Constants.show_type.cat) return null;
    
    const titlePlan = props.result?.plan_to_earn_title || "No";
    const isBrachycephalic = props.result?.is_brachycephalic ? "Yes" : "No";
    
    return { titlePlan, isBrachycephalic };
  };

  const catInfo = getCATInfo();

  return (
    <div className="EnterResults">
      <div className="title">
        <span>Enter Results</span>
        <div className="summaryBox">
          <div className="dogInfo">
            {props.result.call_name}{props.result.arm_number && ` - ${props.result.arm_number}`}
          </div>
          <div className="showInfo">
            {props.result.show_type && props.result.show_type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
            {props.result.show_name && ` - ${props.result.show_name}`}
            {props.result.show_date && ` - ${moment(props.result.show_date).format("MM/DD/YYYY")}`}
          </div>
          {classDisplay && (
            <div className="classInfo">{classDisplay}</div>
          )}
          {catInfo && (
            <div className="catInfo">
              <div>Planning to earn a title: {catInfo.titlePlan}</div>
              <div>Brachycephalic: {catInfo.isBrachycephalic}</div>
            </div>
          )}
        {scentworkConfig && (scentworkConfig.num_hides > 0 || scentworkConfig.time_limit) && (
          <div className="elementConfigSummary">
            {scentworkConfig.num_hides > 0 && (
              <span>Hides: {scentworkConfig.num_hides}</span>
            )}
            {scentworkConfig.time_limit && (
              <span>Time Limit: {scentworkConfig.time_limit}</span>
            )}
          </div>
        )}
        </div>
      </div>
      <form className="results-inner" onSubmit={(e) => { e.preventDefault(); enterResults(); }}>
        {renderResults()}
        <div className="actions">
          <Button type="button" onClick={() => { props.onClose(); }}>Cancel</Button>
          <Button type="submit">Save</Button>
        </div>
      </form>
    </div>
  );
};

export default EnterResults;